/**
 * @fileoverview Connect retained archive search to document/version inspection. Backend search and canonical archive identity own results; local filters/selection own browsing context. Stored-source reads remain active even though live Simul acquisition is retired. `kc-internal-backend/functions/simul_archive.py` owns original/previews and `kc-internal-backend/functions/document_search/service.py` owns search serving.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api from '../../api';
import { AppPageHeader } from '../shared';
import AppIcon from '../shared/AppIcon';
import Button from '../shared/AppPrimeButton';
import { apiUrl, formatCreated, formatSize } from '../Documents/archiveReviewUtils';
import './SimulArchive.css';



function CreatedCell({ value }) {
  const created = formatCreated(value);
  return (
    <time className="simul-archive-version-created" dateTime={created.dateTime} title={created.title}>
      <span>{created.date}</span>
      {created.time && <small>{created.time}</small>}
    </time>
  );
}



function Highlight({ text, term }) {
  if (!term || !text) return <>{text}</>;
  const escaped = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const parts = String(text).split(new RegExp(`(${escaped})`, 'gi'));
  return (
    <>
      {parts.map((part, index) => (
        index % 2 === 1 ?
          <mark key={`${part}-${index}`} className="simul-archive-highlight">{part}</mark> :
          <React.Fragment key={`${part}-${index}`}>{part}</React.Fragment>
      ))}
    </>
  );
}



function SimulArchive() {
  const [documents, setDocuments] = useState([]);
  const [versions, setVersions] = useState([]);
  const [selectedDocumentId, setSelectedDocumentId] = useState('');
  const [selectedVersionId, setSelectedVersionId] = useState('');
  const [previewVersionId, setPreviewVersionId] = useState('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [collection, setCollection] = useState('');
  const [archivedFilter, setArchivedFilter] = useState('');
  const [author, setAuthor] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [loading, setLoading] = useState(true);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [error, setError] = useState('');
  const requestRef = useRef(0);
  const versionsRequestRef = useRef(0);

  useEffect(() => {
    document.title = 'Simul Archive';
  }, []);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => window.clearTimeout(timeoutId);
  }, [search]);

  const loadDocuments = useCallback(

  () => {
    const requestId = ++requestRef.current;
    setLoading(true);
    setError('');
    const params = new URLSearchParams();
    params.set('first', '0');
    params.set('rows', '50');
    if (debouncedSearch) params.set('search', debouncedSearch);
    if (collection.trim()) params.set('collection', collection.trim());
    if (archivedFilter) params.set('archived', archivedFilter);
    if (author.trim()) params.set('author', author.trim());
    if (dateFrom) params.set('date_from', dateFrom);
    if (dateTo) params.set('date_to', dateTo);

    return api.get(`/simul/archive/search?${params.toString()}`)
      .then(

      (response) => {
        if (requestId !== requestRef.current) return;
        const nextDocuments = response.data?.documents || [];
        setDocuments(nextDocuments);
        setSelectedDocumentId((current) => {
          if (current && nextDocuments.some((doc) => doc.document_id === current)) return current;
          return nextDocuments[0]?.document_id || '';
        });
      })
      .catch(

      (err) => {
        if (requestId !== requestRef.current) return;
        console.error('Failed to search Simul archive:', err);
        setError(err?.response?.data?.error || err.message || 'Failed to load Simul archive.');
        setDocuments([]);
      })
      .finally(() => {
        if (requestId === requestRef.current) setLoading(false);
      });
  }, [archivedFilter, author, collection, dateFrom, dateTo, debouncedSearch]);

  const loadVersions = useCallback(

  (documentId) => {
    if (!documentId) {
      setVersions([]);
      setSelectedVersionId('');
      setPreviewVersionId('');
      return Promise.resolve();
    }
    const requestId = ++versionsRequestRef.current;
    setVersionsLoading(true);
    setPreviewVersionId('');
    return api.get(`/simul/archive/documents/${encodeURIComponent(documentId)}/versions`)
      .then(

      (response) => {
        if (requestId !== versionsRequestRef.current) return;
        const nextVersions = response.data?.versions || [];
        const latest = nextVersions.find((version) => version.is_latest);
        const defaultRevisionId = latest?.revision_id || nextVersions[0]?.revision_id || '';
        setVersions(nextVersions);
        setSelectedVersionId(defaultRevisionId);
        setPreviewVersionId(defaultRevisionId);
      })
      .catch(

      (err) => {
        if (requestId !== versionsRequestRef.current) return;
        console.error('Failed to load Simul archive versions:', err);
        setVersions([]);
        setSelectedVersionId('');
        setPreviewVersionId('');
      })
      .finally(() => {
        if (requestId === versionsRequestRef.current) setVersionsLoading(false);
      });
  }, []);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);


  useEffect(() => {
    loadVersions(selectedDocumentId);
  }, [loadVersions, selectedDocumentId]);

  const selectedDocument = useMemo(
    () => documents.find((doc) => doc.document_id === selectedDocumentId) || null,
    [documents, selectedDocumentId]
  );

  const collections = useMemo(() => {
    const values = new Set();
    for (const doc of documents) {
      if (doc.collection) values.add(doc.collection);
    }
    return [...values].sort();
  }, [documents]);

  const matchingVersions = useMemo(

  () => {
    const fromSearch = selectedDocument?.versions || [];
    if (!fromSearch.length) return versions;
    const byId = new Map(versions.map((version) => [version.revision_id, version]));
    return fromSearch.map((version) => ({ ...(byId.get(version.revision_id) || {}), ...version }));
  }, [selectedDocument, versions]);



  const openVersionPreview =

  (revisionId) => {
    setSelectedVersionId(revisionId);
    setPreviewVersionId(revisionId);
  };

  const handleVersionKeyDown =

  (event, revisionId) => {
    if (event.target !== event.currentTarget) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openVersionPreview(revisionId);
    }
  };

  const clearFilters =

  () => {
    setSearch('');
    setCollection('');
    setArchivedFilter('');
    setAuthor('');
    setDateFrom('');
    setDateTo('');
  };

  return (
    <div className="simul-archive-page">
      <AppPageHeader
        eyebrow="Workspace Tools"
        title="Simul Archive"
        icon="pi pi-history text-xl" />

      <main className="simul-archive-shell">

        <section className="simul-archive-filters" aria-label="Simul archive filters">
          <label>
            <span>Search</span>
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Filename, author, revision, or document text" />
          </label>
          <label>
            <span>Collection</span>
            <input
              list="simul-archive-collections"
              value={collection}
              onChange={(event) => setCollection(event.target.value)}
              placeholder="Any collection" />
            <datalist id="simul-archive-collections">
              {collections.map((value) => <option key={value} value={value} />)}
            </datalist>
          </label>
          <label>
            <span>Archived</span>
            <select value={archivedFilter} onChange={(event) => setArchivedFilter(event.target.value)}>
              <option value="">Any</option>
              <option value="true">Archived in Simul</option>
              <option value="false">Not archived</option>
            </select>
          </label>
          <label>
            <span>Author</span>
            <input
              type="search"
              value={author}
              onChange={(event) => setAuthor(event.target.value)}
              placeholder="Any author" />
          </label>
          <label>
            <span>From</span>
            <input
              type="date"
              value={dateFrom}
              onChange={(event) => setDateFrom(event.target.value)} />
          </label>
          <label>
            <span>To</span>
            <input
              type="date"
              value={dateTo}
              onChange={(event) => setDateTo(event.target.value)} />
          </label>
          <div className="simul-archive-filter-actions">
            <Button label="Refresh" icon="pi pi-refresh" severity="secondary" onClick={loadDocuments} />
            <button type="button" className="simul-archive-link-button" onClick={clearFilters}>Clear filters</button>
          </div>
        </section>

        {error && <div className="simul-archive-error">{error}</div>}

        <section className="simul-archive-content">
          <div className="simul-archive-documents" aria-label="Simul archive documents">
            <div className="simul-archive-section-header">
              <h2>Documents</h2>
              <span>{loading ? 'Loading' : `${documents.length} shown`}</span>
            </div>
            {loading ? (
              <div className="simul-archive-empty">Loading Simul archive documents...</div>
            ) : documents.length === 0 ? (
              <div className="simul-archive-empty">No archived Simul documents found.</div>
            ) : (
              <div className="simul-archive-document-list">
                {documents.map((doc) => (
                  <button
                    type="button"
                    key={doc.document_id}
                    className={`simul-archive-document-row ${doc.document_id === selectedDocumentId ? 'is-selected' : ''}`}
                    onClick={() => setSelectedDocumentId(doc.document_id)}>
                    <span className="simul-archive-document-name" title={doc.filename}>
                      <Highlight text={doc.filename || 'Untitled document'} term={debouncedSearch} />
                    </span>
                    <span className="simul-archive-document-version-count">{doc.version_count || 0} versions</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="simul-archive-detail" aria-label="Simul archive document versions">
            <div className="simul-archive-section-header">
              <h2>{selectedDocument?.filename || 'Versions'}</h2>
              <span>{versionsLoading ? 'Loading' : `${versions.length} versions`}</span>
            </div>

            {!selectedDocument ? (
              <div className="simul-archive-empty">Select a document to inspect versions.</div>
            ) : (
              <>
                <div className="simul-archive-document-meta">
                  <span><AppIcon icon="pi pi-folder" />{selectedDocument.collection || '-'}</span>
                  <span><AppIcon icon="pi pi-file" />{selectedDocument.document_id}</span>
                  <span><AppIcon icon="pi pi-clock" />Latest: {selectedDocument.latest_revision_id || '-'}</span>
                </div>

                {versionsLoading ? (
                  <div className="simul-archive-empty">Loading version history...</div>
                ) : (
                  <div className="simul-archive-version-table">
                    <div className="simul-archive-version-heading">
                      <span>Version</span>
                      <span>Created</span>
                      <span>Author</span>
                      <span>Size</span>
                      <span>Actions</span>
                    </div>
                    {matchingVersions.map((version) => (
                      <div
                        key={version.revision_id}
                        className={`simul-archive-version-row ${version.revision_id === selectedVersionId ? 'is-selected' : ''}`}
                        role="button"
                        tabIndex={0}
                        aria-label={`Preview ${version.label || version.revision_id}`}
                        onClick={() => openVersionPreview(version.revision_id)}
                        onKeyDown={(event) => handleVersionKeyDown(event, version.revision_id)}>
                        <div className="simul-archive-version-title">
                          <span>
                            {version.label || `Version ${Number(version.version_index || 0) + 1}`}
                            {version.is_latest && <em>Latest</em>}
                          </span>
                          <small>{version.revision_id}</small>
                        </div>
                        <CreatedCell value={version.created_at} />
                        <span title={version.created_by || '-'}>{version.created_by || '-'}</span>
                        <span className="simul-archive-version-size">{formatSize(version.size)}</span>
                        <span className="simul-archive-version-actions">
                          <a
                            href={apiUrl(`/simul/archive/versions/${encodeURIComponent(version.revision_id)}/download`)}
                            target="_blank"
                            rel="noreferrer"
                            onClick={(event) => event.stopPropagation()}>
                            Download
                          </a>
                        </span>
                        {version.snippets?.length > 0 && (
                          <div className="simul-archive-snippets">
                            {version.snippets.slice(0, 2).map((snippet, index) => (
                              <p key={`${version.revision_id}-${index}`}>
                                <Highlight text={snippet.text} term={debouncedSearch} />
                              </p>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {previewVersionId && (
                  <div className="simul-archive-preview-panel">
                    <div className="simul-archive-preview-header">
                      <strong>Preview</strong>
                      <button type="button" onClick={() => setPreviewVersionId('')}>Close</button>
                    </div>
                    <iframe
                      title="Simul archived version preview"
                      src={apiUrl(`/simul/archive/versions/${encodeURIComponent(previewVersionId)}/preview-pdf`)} />
                  </div>
                )}
              </>
            )}
          </div>
        </section>
      </main>
    </div>
  );
}

export default SimulArchive;
